import { useEffect, useRef, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';

// Small confirm dialog for approving or rejecting an AI proposal with a decision note.
// The note is optional for approvals and required for rejections.
function AiProposalDecisionDialog({ open, mode, summary = '', onCancel, onConfirm }) {
  const noteInputRef = useRef(null);
  const [noteError, setNoteError] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const isReject = mode === 'reject';

  // Reset validation and the note input whenever the dialog opens
  useEffect(() => {
    if (!open) return;
    setNoteError(false);
    setSubmitting(false);
    if (noteInputRef.current) noteInputRef.current.value = '';
  }, [open]);

  const handleConfirm = async () => {
    const note = noteInputRef.current?.value?.trim() || '';
    if (isReject && !note) {
      setNoteError(true);
      return;
    }
    setNoteError(false);
    setSubmitting(true);
    try {
      await onConfirm(note);
    } catch {
      // Error toast is shown inside the hook; keep the dialog open for a retry
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onCancel} fullWidth maxWidth='xs'>
      <DialogTitle>{isReject ? 'Vorschlag ablehnen' : 'Vorschlag annehmen'}</DialogTitle>
      <DialogContent dividers>
        {summary ? (
          <Typography variant='body2' sx={{ mb: 2, color: 'text.secondary' }}>
            {summary}
          </Typography>
        ) : null}
        <TextField
          fullWidth
          multiline
          minRows={2}
          size='small'
          label={isReject ? 'Ablehnungsgrund' : 'Notiz (optional)'}
          inputRef={noteInputRef}
          required={isReject}
          error={noteError}
          helperText={noteError ? 'Bitte gib einen Ablehnungsgrund an' : ' '}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={submitting}>
          Abbrechen
        </Button>
        <Button
          variant='contained'
          color={isReject ? 'error' : 'success'}
          onClick={handleConfirm}
          disabled={submitting}
        >
          {isReject ? 'Ablehnen' : 'Annehmen'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AiProposalDecisionDialog;
